import React, { useState } from "react";

const emptyRow = { name: "", position: "", locationToday: "", locationTomorrow: "" };

const inlineInput =
  "w-full rounded border border-zinc-300 bg-white px-2 py-1 text-sm focus:border-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-600/20";

const PersonnelTable = ({ personnel, dprId, onCreate, onUpdate, onDelete, onCopyPrevious }) => {
  const [newRow, setNewRow] = useState(emptyRow);
  const [editingId, setEditingId] = useState(null);
  const [editField, setEditField] = useState(null);
  const [draftValue, setDraftValue] = useState("");

  const handleAdd = () => {
    if (!newRow.name.trim()) return;
    onCreate({
      dprId,
      name: newRow.name.trim(),
      position: newRow.position.trim(),
      locationToday: newRow.locationToday.trim(),
      locationTomorrow: newRow.locationTomorrow.trim(),
      sortOrder: personnel.length,
    });
    setNewRow(emptyRow);
  };

  const handleStartEdit = (id, field, value) => {
    setEditingId(id);
    setEditField(field);
    setDraftValue(value ?? "");
  };

  const handleCommit = () => {
    if (!editingId || !editField) return;
    const entry = personnel.find((p) => p.id === editingId);
    if (entry) {
      onUpdate(editingId, { ...entry, [editField]: draftValue });
    }
    setEditingId(null);
    setEditField(null);
    setDraftValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleCommit();
    if (e.key === "Escape") {
      setEditingId(null);
      setEditField(null);
    }
  };

  const renderCell = (row, field) => {
    const isEditing = editingId === row.id && editField === field;
    if (isEditing) {
      return (
        <input
          type="text"
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={handleKeyDown}
          autoFocus
          onFocus={(e) => e.target.select()}
          className="w-full rounded border border-navy-600 bg-white px-1.5 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy-600/20"
        />
      );
    }
    return (
      <div
        onClick={() => handleStartEdit(row.id, field, row[field])}
        className="min-h-[1.5em] cursor-pointer rounded px-1 -mx-1 hover:bg-navy-50/60"
      >
        {row[field] || <span className="text-zinc-300">—</span>}
      </div>
    );
  };

  return (
    <section>
      <header className="mb-3 flex items-baseline justify-between">
        <h3 className="section-eyebrow">Personnel in field</h3>
        {onCopyPrevious && (
          <button
            type="button"
            onClick={onCopyPrevious}
            className="text-xs font-medium text-navy-600 hover:text-navy-700"
          >
            Copy from previous DPR
          </button>
        )}
      </header>
      <table className="table-hairline">
        <thead>
          <tr>
            <th>Name</th>
            <th>Position</th>
            <th>Location (today)</th>
            <th>Location (tomorrow)</th>
            <th className="w-16 text-right" />
          </tr>
        </thead>
        <tbody>
          {personnel.map((row) => (
            <tr key={row.id}>
              <td>{renderCell(row, "name")}</td>
              <td>{renderCell(row, "position")}</td>
              <td>{renderCell(row, "locationToday")}</td>
              <td>{renderCell(row, "locationTomorrow")}</td>
              <td className="text-right">
                <button
                  type="button"
                  className="text-xs font-medium text-zinc-400 hover:text-rust-600"
                  onClick={() => onDelete(row.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          <tr>
            {["name", "position", "locationToday", "locationTomorrow"].map(
              (field) => (
                <td key={field}>
                  <input
                    type="text"
                    value={newRow[field]}
                    onChange={(e) =>
                      setNewRow((prev) => ({ ...prev, [field]: e.target.value }))
                    }
                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    placeholder={
                      field === "name"
                        ? "Name"
                        : field === "position"
                        ? "Position"
                        : field === "locationToday"
                        ? "Today"
                        : "Tomorrow"
                    }
                    className={inlineInput}
                  />
                </td>
              )
            )}
            <td className="text-right">
              <button
                type="button"
                onClick={handleAdd}
                disabled={!newRow.name.trim()}
                className="text-xs font-medium text-navy-600 hover:text-navy-700 disabled:opacity-40"
              >
                Add
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
};

export default PersonnelTable;
